<script setup lang="ts">
import type { Track, ArtistCredit, Album, Playlist, Library, Channel, Actor } from '~/types'
import type { PlayOptionsProps } from '~/composables/audio/usePlayOptions'

import { computed, ref } from 'vue'

import usePlayOptions from '~/composables/audio/usePlayOptions'

import { usePlayer } from '~/composables/audio/player'
import { useQueue } from '~/composables/audio/queue'
import { useStore } from '~/store'
import TrackFavoriteIcon from '~/components/favorites/TrackFavoriteIcon.vue'
import PlayIndicator from '~/components/audio/track/PlayIndicator.vue'
import PlayButton from '~/components/audio/PlayButton.vue'

import Button from '~/ui/Button.vue'
import Link from '~/ui/Link.vue'

import { to } from '~/routing'

const store = useStore()

interface Props extends PlayOptionsProps {
  track: Track
  index: number

  showAlbum?: boolean
  showArt?: boolean
  showArtist?: boolean
  showDuration?: boolean
  showPosition?: boolean
  displayActions?: boolean

  // TODO(wvffle): Remove after https://github.com/vuejs/core/pull/4512 is merged
  tracks: Track[]
  isPlayable?: boolean
  artistCredit?: ArtistCredit[] | null
  album?: Album | null
  playlist?: Playlist | null
  library?: Library | null
  channel?: Channel | null
  account?: Actor | null
}

const props = withDefaults(defineProps<Props>(), {
  showAlbum: true,
  showArt: true,
  showArtist: true,
  showDuration: true,
  showPosition: false,
  displayActions: true,

  artistCredit: null,
  album: null,
  playlist: null,
  library: null,
  channel: null,
  account: null
})

const { activateTrack } = usePlayOptions(props)
const { isPlaying } = usePlayer()
const { currentTrack } = useQueue()

const active = computed(() => props.track.id === currentTrack.value?.id && props.track.position === currentTrack.value?.position)
const isFavorite = computed(() => store.getters['favorites/isFavorite'](props.track.id))

// JS hover tracking: the row uses display:contents so the margin gaps between
// cells have no element box — :has(:hover) briefly drops to false while crossing
// them. A debounced mouseleave avoids the flicker.
//
// TODO: Remove this hack and use CSS with delayed transition - or fix underlying rerendering order issue
const isHovered = ref(false)
let leaveTimer: ReturnType<typeof setTimeout> | null = null
const onMouseEnter = () => {
  if (leaveTimer) { clearTimeout(leaveTimer); leaveTimer = null }
  isHovered.value = true
}
const onMouseLeave = () => {
  leaveTimer = setTimeout(() => { isHovered.value = false }, 60)
}
</script>

<template>
  <div
    :class="[$style.row, !track.is_playable && $style.unplayable, isHovered && $style.hovered, active && 'color-accent']"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @click="track.is_playable && (active ? isPlaying = !isPlaying : activateTrack(track))"
  >
    <!-- Play / position column -->

    <div
      :class="$style['play-col']"
    >
      <play-indicator v-if="isPlaying && active" />

      <Button
        v-if="!isPlaying && active"
        :class="$style.overlay"
        class="color-default-solid shape-square"
      >
        <Icon icon="bi:play-fill" />
      </Button>
      <Button
        v-else-if="isPlaying && active"
        :class="[$style.overlay, $style['show-on-hover']]"
        class="color-default-solid shape-square"
        @click.stop="isPlaying=false"
      >
        <Icon icon="bi:pause-fill" />
      </Button>
      <Button
        v-else-if="track.is_playable"
        :class="[$style.overlay, $style['show-on-hover']]"
        class="color-default-solid shape-square"
      >
        <Icon icon="bi:play-fill" />
      </Button>

      <span v-if="showPosition && !active" :class="[$style.position, $style['hide-on-hover']]">
        {{ `${index + 1}`.padStart(2, '0') }}
      </span>
    </div>

    <!-- Art column -->

    <div :class="$style['art-col']">
      <img
        v-if="showArt && track.cover?.urls.original"
        v-lazy="store.getters['instance/absoluteUrl'](track.cover.urls.small_square_crop)"
        :alt="track.title"
        :class="$style.image"
      >
      <img
        v-else-if="showArt && track.album?.cover?.urls.original"
        v-lazy="store.getters['instance/absoluteUrl'](track.album.cover.urls.small_square_crop)"
        alt=""
        :class="$style.image"
      >
      <img
        v-else-if="showArt"
        alt=""
        :class="$style.image"
        src="../../../assets/audio/default-cover.png"
      >
    </div>

    <!-- Title column -->

    <div tabindex="0" :class="$style['text-col']">
      <span>{{ track.title }}</span>
    </div>

    <!-- Album column -->

    <div :class="$style['text-col']">
      <Link
        v-if="showAlbum && track.album"
        class="color-default"
        v-bind="to({ name: 'library.albums.detail', params: { id: track.album.id } })"
        @click.stop
      >
        {{ track.album.title }}
      </Link>
    </div>

    <!-- Artist column -->

    <div :class="$style['text-col']">
      <template
        v-for="ac in (showArtist ? track.artist_credit : [])"
        :key="ac.artist.id"
      >
        <Link
          class="color-default"
          v-bind="to({ name: 'library.artists.detail', params: { id: ac.artist?.id } })"
          @click.stop
        >
          {{ ac.credit }}
        </Link>
        <span>{{ ac.joinphrase }}</span>
      </template>
    </div>

    <!-- Favorite column -->

    <div :class="$style['meta-col']">
      <track-favorite-icon
        v-if="store.state.auth.authenticated && track.is_playable"
        :class="[!isFavorite && $style['show-on-hover']]"
        :track="track"
        ui-block-size="48"
        class="color-default-ghost"
        @click.stop
      />
    </div>

    <!-- Duration column -->

    <div class="layout-row">
      <human-duration
        v-if="showDuration && track.uploads && track.uploads.length > 0 && track.uploads[0]!.duration"
        :duration="track.uploads[0]!.duration"
      />
    </div>

    <!-- Actions column -->

    <div v-if="displayActions">
      <PlayButton
        :class="$style['show-on-hover']"
        :dropdown-only="true"
        :is-playable="track.is_playable"
        :track="track"
        class="color-default-solid shape-square"
        @click.stop
      />
    </div>
  </div>
</template>

<style module>

  .row {
    display: contents !important;
  }


  .play-col {
    position: relative;
  }

  .text-col {
    align-content: center;
  }

  /* Buttons overlay the track-position number */
  .overlay {
    position: absolute;
    inset: 0;
    cursor: pointer;
  }

  /* Track position number */
  .position {
    font-size: 0.85em;
    opacity: 0.6;
  }

  .image {
    width: var(--block-size-48);
    height: var(--block-size-48);
    border-radius: var(--fw-border-radius);
  }

  /* Show-on-hover / hide-on-hover (driven by JS isHovered to avoid gaps between cells) */
  .row:not(.hovered) .show-on-hover,
  .row.hovered .hide-on-hover {
    opacity: 0;
    pointer-events: none;
  }

  /* Unplayable rows */
  .unplayable .play-col,
  .unplayable .art-col,
  .unplayable .text-col,
  .unplayable .meta-col {
    opacity: 0.4;
    cursor: default;
  }
</style>
